import { _ } from "../../../../core/polyglot/polyglot.js";
import {
	notBlank,
	required,
} from "../../../../core/widgets/forms/validators.js";
import FormBuilder from "../../../../core/widgets/forms/FormBuilder.js";
import ReportTemplateVisitor from "../../../../models/reports/templates/ReportTemplateVisitor.js";
import TranslatedString from "../../../../models/TranslatedString.js";

/**
 * Visitor responsible for building a specialized editor for report
 * template items.
 */
export default class ReportTemplateItemEditorBuilder extends ReportTemplateVisitor {
	visitAccountRowTemplate(row) {
		return (context) => {
			const form = new FormBuilder({
				model: row,
				showActions: false,
			});

			form.newDropDownField({
				label: _("Account"),
				liveSearch: true,
				accessor: row.getAccountNumber.bind(row),
				mutator: row.setAccountNumber.bind(row),
				selected: row.getAccountNumber(),
				items: context
					.getPeriod()
					.getAccounts()
					.map((account) => ({
						label: account.toString(),
						value: account.getNumber(),
					})),
			});

			return form;
		};
	}

	visitGroupRowTemplate(row) {
		return () => {
			const form = new FormBuilder({
				model: row,
				showActions: false,
			});

			form.input({
				label: _("Title"),
				accessor: () => row.getTitle().toString(),
				mutator: (title) =>
					row.setTitle(new TranslatedString({ value: title })),
				validator: notBlank(),
			});

			return form;
		};
	}

	visitRepeatingRowTemplate() {
		return null;
	}

	visitAccountIntervalGroupRowTemplate(row) {
		return () => {
			const form = new FormBuilder({
				model: row,
				showActions: false,
			});

			form.input({
				label: _("Title"),
				accessor: () => row.getTitle(),
				mutator: (state) => row.setTitle(state),
				validators: notBlank(),
			});

			form.input({
				label: _("Intervals"),
				accessor: () => row.getInterval(),
				mutator: (state) => row.setInterval(state),
				placeholder: "3010-3100",
				help: _(
					'Enter one or many intervals. Example: "3000-3100,3200-3300"',
				),
				validator: notBlank(),
			});

			return form;
		};
	}

	visitAccountGroupRowTemplate(row) {
		return (context) => {
			const form = new FormBuilder({
				model: row,
				showActions: false,
			});

			const accountGroups = context.getChartOfAccounts().getAllGroups();

			form.tree({
				label: _("Group"),
				autoOpen: 0,
				accessor: () =>
					accountGroups.find(
						(group) => group.getId() === row.getAccountGroupId(),
					),
				mutator: (accountGroup) => {
					row.setAccountGroupId(accountGroup.getId());
				},
				data: context.getChartOfAccounts().getMainTree().getChildren(),
				getChildren: (item) =>
					item
						.getChildren()
						.filter((child) => child.isAccountGroup()),
				getLabel: (item) => item.getName(),
				validator: required(),
			});

			return form;
		};
	}

	visitKeyFigureRowTemplate(row) {
		return (context) => {
			const form = new FormBuilder({ model: row, showActions: false });

			form.keyFigurePickerField({
				keyFigures: context.getVisibleKeyFigures(),
				accessor: () => context.findKeyFigure(row.getKeyFigureId()),
				mutator: (keyFigure) => row.setKeyFigure(keyFigure),
			});

			return form;
		};
	}

	visitTextRowTemplate(row) {
		return () => {
			const form = new FormBuilder({
				model: row,
				showActions: false,
			});

			form.input({
				label: _("Text"),
				name: "text",
				accessor: () => row.getTitle(),
				mutator: (state) => row.setTitle(state),
				validator: notBlank(),
			});

			return form;
		};
	}

	visitFormulaRowTemplate(row) {
		return this._createTitleEditionForm({
			item: row,
			accessor: () => row.getTitle().currentTranslation(),
			mutator: (title) =>
				row.setTitle(new TranslatedString({ value: title })),
		});
	}

	visitFormulaColumnTemplate(column) {
		return this._createTitleEditionForm({
			item: column,
			accessor: () => column.getCustomTitle(),
			mutator: (title) => column.setCustomTitle(title),
		});
	}

	_createTitleEditionForm({ item, accessor, mutator }) {
		return () => {
			const form = new FormBuilder({
				model: item,
				showActions: false,
			});

			form.input({
				label: _("Title"),
				name: "title",
				accessor,
				mutator,
				validator: notBlank(),
			});

			return form;
		};
	}
}
